// bvtTableBuilders.js

// -----------------------
// SHARED UTILITY HELPERS
// -----------------------
function calculateVariance(a, b) {
    if (typeof a === "number" && typeof b === "number") {
      return b - a;
    }
    return null;
  }

// NEW: Helper to create table row data for common pattern
export function createTableRowData(item, baseline, penultimate, current, type) {
  const baselineValue = baseline.contract_structure[type]?.[item] || 0;
  const penultimateValue = penultimate.contract_structure[type]?.[item] || 0;
  const currentValue = current.contract_structure[type]?.[item] || 0;
  
  return {
    item,
    baselineValue,
    penultimateValue,
    currentValue,
    variancePenultimateToCurrent: calculateVariance(penultimateValue, currentValue),
    varianceBaselineToCurrent: calculateVariance(baselineValue, currentValue)
  };
}

// NEW: Helper to get filtered items by category
export function getFilteredItems(items, filterType) {
  switch (filterType) {
    case 'LS':
      return items.filter(item => item.includes('LS'));
    case 'GMP':
      return items.filter(item => item.includes('GMP'));
    default:
      return items;
  }
}

// NEW: Helper to create section header row data
export function createSectionHeader(title) {
  return {
    item: title,
    baselineValue: '',
    penultimateValue: '',
    currentValue: '',
    variancePenultimateToCurrent: '',
    varianceBaselineToCurrent: '',
    isSectionHeader: true
  };
}

// NEW: Snapshot processing utilities
export function processSnapshots(selectedSnapshots) {
  const sortedSnapshots = [...(selectedSnapshots || [])].sort((a, b) => 
    new Date(a.created_at) - new Date(b.created_at)
  );
  
  return {
    sortedSnapshots,
    defaultBaselineId: sortedSnapshots[0]?.id || null,
    defaultCurrentId: sortedSnapshots[sortedSnapshots.length - 1]?.id || null,
    defaultPenultimateId: sortedSnapshots[sortedSnapshots.length - 2]?.id || null
  };
}

export function getSnapshotDetails(sortedSnapshots, baselineId, penultimateId, currentId) {
  const snapshotAObj = sortedSnapshots.find(s => s.id === penultimateId);
  const snapshotBObj = sortedSnapshots.find(s => s.id === currentId);
  const baseSnapshotObj = sortedSnapshots.find(s => s.id === baselineId);

  return {
    snapshotAObj,
    snapshotBObj, 
    baseSnapshotObj,
    snapshotADate: snapshotAObj ? formatDateShort(snapshotAObj.created_at) : "Date Not Found",
    snapshotBDate: snapshotBObj ? formatDateShort(snapshotBObj.created_at) : "Date Not Found",
    baseSnapshotDate: baseSnapshotObj ? formatDateShort(baseSnapshotObj.created_at) : "Date Not Found"
  };
}

// Note: formatDateShort needs to be imported where this is used
  
  function calculateTotalsForType(contractSummary, type) {
    // e.g. contractSummary.contract_structure.Budget or .Cost
    const structure = contractSummary?.contract_structure?.[type] || {};
    const items = Object.keys(structure);
    let lumpSum = 0, gmp = 0;
    items.forEach((item) => {
      const val = structure[item] || 0;
      if (item.includes("LS")) {
        lumpSum += val;
      } else if (item.includes("GMP")) {
        gmp += val;
      }
    });
    return { lumpSum, gmp };
  }
  
  function accumulateAllowanceAndMarkups(baseline, penultimate, current, type) {
    let baselineAllowance = 0,
      penultimateAllowance = 0,
      currentAllowance = 0;
    let baselineMarkups = 0,
      penultimateMarkups = 0,
      currentMarkups = 0;
  
    const bObj = baseline.contract_structure?.[type] || {};
    const pObj = penultimate.contract_structure?.[type] || {};
    const cObj = current.contract_structure?.[type] || {};
    const allKeys = new Set([
      ...Object.keys(bObj),
      ...Object.keys(pObj),
      ...Object.keys(cObj),
    ]);
  
    allKeys.forEach((key) => {
      const bVal = bObj[key] || 0;
      const pVal = pObj[key] || 0;
      const cVal = cObj[key] || 0;
      if (key === "Allowance") {
        baselineAllowance += bVal;
        penultimateAllowance += pVal;
        currentAllowance += cVal;
      } else if (key === "Financial Markups") {
        baselineMarkups += bVal;
        penultimateMarkups += pVal;
        currentMarkups += cVal;
      }
    });
  
    return {
      baselineAllowance,
      penultimateAllowance,
      currentAllowance,
      baselineMarkups,
      penultimateMarkups,
      currentMarkups,
    };
  }
  
  function getOverUnderItems(baseline, penultimate, current) {
    const baseOU = baseline?.contract_structure?.["Over/Under"] || {};
    const penOU = penultimate?.contract_structure?.["Over/Under"] || {};
    const curOU = current?.contract_structure?.["Over/Under"] || {};
    const allKeys = new Set([...Object.keys(baseOU), ...Object.keys(penOU), ...Object.keys(curOU)]);
    return { baseOU, penOU, curOU, allKeys };
  }
  
  function getJTDCostsItems(baseline, penultimate, current) {
    const baseJTD = baseline?.contract_structure?.["JTD Costs"] || {};
    const penJTD = penultimate?.contract_structure?.["JTD Costs"] || {};
    const curJTD = current?.contract_structure?.["JTD Costs"] || {};
    return { baseJTD, penJTD, curJTD };
  }
  
  // ---------------------------------------------------------
  // 1) BUILD BUDGET TABLE
  // ---------------------------------------------------------
  export function buildBudgetTableData(budgetData) {
    if (
      !budgetData?.baseline_contract_summary ||
      !budgetData?.penultimate_contract_summary ||
      !budgetData?.current_contract_summary
    ) {
      return { head: [["No Budget Data"]], body: [] };
    }
    const { baseline_contract_summary: baseline, penultimate_contract_summary: penultimate, current_contract_summary: current } = budgetData;
  
    // The columns: Category | Baseline | Snapshot A | Snapshot B | A->B Var | Baseline->B Var
    const head = [[
      "Category",
      "Baseline",
      "Snapshot A",
      "Snapshot B",
      "Snapshot A to B Variance",
      "Baseline to Snapshot B Variance",
    ]];
    const body = [];
  
    // lumpsum & gmp totals
    const baseBudgetTotals = calculateTotalsForType(baseline, "Budget");
    const penBudgetTotals  = calculateTotalsForType(penultimate, "Budget");
    const currBudgetTotals = calculateTotalsForType(current, "Budget");
  
    // allowance & markups
    const {
      baselineAllowance,
      penultimateAllowance,
      currentAllowance,
      baselineMarkups,
      penultimateMarkups,
      currentMarkups,
    } = accumulateAllowanceAndMarkups(baseline, penultimate, current, "Budget");
  
    // Section row: "Lump Sum Budget"
    body.push([
      { content: "Lump Sum Budget", sectionHeader: true },
      { content: "" },
      { content: "" },
      { content: "" },
      { content: "" },
      { content: "" },
    ]);
  
    // Each lumpsum budget item
    const currentBudgetItems = Object.keys(current.contract_structure.Budget || {});
    const lumpsumItems = currentBudgetItems.filter((item) => item.includes("LS"));
    lumpsumItems.forEach((item) => {
      const bVal = baseline.contract_structure.Budget[item] || 0;
      const pVal = penultimate.contract_structure.Budget[item] || 0;
      const cVal = current.contract_structure.Budget[item] || 0;
      const varPenToCur = calculateVariance(pVal, cVal);
      const varBaseToCur= calculateVariance(bVal, cVal);
  
      body.push([
        { content: item },
        { content: bVal.toString() },
        { content: pVal.toString() },
        { content: cVal.toString() },
        { content: varPenToCur?.toString() || "0" },
        { content: varBaseToCur?.toString() || "0" },
      ]);
    });
  
    // Lump Sum Budget Total
    body.push([
      { content: "Lump Sum Budget Total", isTotal: true },
      { content: baseBudgetTotals.lumpSum.toString() },
      { content: penBudgetTotals.lumpSum.toString() },
      { content: currBudgetTotals.lumpSum.toString() },
      {
        content: calculateVariance(penBudgetTotals.lumpSum, currBudgetTotals.lumpSum)?.toString() || "0",
      },
      {
        content: calculateVariance(baseBudgetTotals.lumpSum, currBudgetTotals.lumpSum)?.toString() || "0",
      },
    ]);
  
    // Section row: "GMP Budget"
    body.push([
      { content: "GMP Budget", sectionHeader: true },
      { content: "" },
      { content: "" },
      { content: "" },
      { content: "" },
      { content: "" },
    ]);
    // each GMP item
    const gmpItems = currentBudgetItems.filter((item) => item.includes("GMP"));
    gmpItems.forEach((item) => {
      const bVal = baseline.contract_structure.Budget[item] || 0;
      const pVal = penultimate.contract_structure.Budget[item] || 0;
      const cVal = current.contract_structure.Budget[item] || 0;
      const varPenToCur = calculateVariance(pVal, cVal);
      const varBaseToCur= calculateVariance(bVal, cVal);
      body.push([
        { content: item },
        { content: bVal.toString() },
        { content: pVal.toString() },
        { content: cVal.toString() },
        { content: varPenToCur?.toString() || "0" },
        { content: varBaseToCur?.toString() || "0" },
      ]);
    });
  
    // GMP Budget Total
    body.push([
      { content: "GMP Budget Total", isTotal: true },
      { content: baseBudgetTotals.gmp.toString() },
      { content: penBudgetTotals.gmp.toString() },
      { content: currBudgetTotals.gmp.toString() },
      {
        content: calculateVariance(penBudgetTotals.gmp, currBudgetTotals.gmp)?.toString() || "0",
      },
      {
        content: calculateVariance(baseBudgetTotals.gmp, currBudgetTotals.gmp)?.toString() || "0",
      },
    ]);
  
    // Allowance Budget
    body.push([
      { content: "Allowance Budget" },
      { content: baselineAllowance.toString() },
      { content: penultimateAllowance.toString() },
      { content: currentAllowance.toString() },
      {
        content: calculateVariance(penultimateAllowance, currentAllowance)?.toString() || "0",
      },
      {
        content: calculateVariance(baselineAllowance, currentAllowance)?.toString() || "0",
      },
    ]);
  
    // Financial Markups Budget
    body.push([
      { content: "Financial Markups Budget" },
      { content: baselineMarkups.toString() },
      { content: penultimateMarkups.toString() },
      { content: currentMarkups.toString() },
      {
        content: calculateVariance(penultimateMarkups, currentMarkups)?.toString() || "0",
      },
      {
        content: calculateVariance(baselineMarkups, currentMarkups)?.toString() || "0",
      },
    ]);
  
    // Final row: "Total Budget Contract Values"
    const baseOverall = baseBudgetTotals.lumpSum + baseBudgetTotals.gmp + baselineAllowance + baselineMarkups;
    const penOverall  = penBudgetTotals.lumpSum + penBudgetTotals.gmp + penultimateAllowance + penultimateMarkups;
    const currOverall = currBudgetTotals.lumpSum + currBudgetTotals.gmp + currentAllowance + currentMarkups;
  
    body.push([
      { content: "Total Budget Contract Values", isTotal: true, borderType: "double" },
      { content: baseOverall.toString() },
      { content: penOverall.toString() },
      { content: currOverall.toString() },
      { content: calculateVariance(penOverall, currOverall)?.toString() || "0" },
      { content: calculateVariance(baseOverall, currOverall)?.toString() || "0" },
    ]);
  
    return { head, body };
  }
  
  // ---------------------------------------------------------
  // 2) BUILD COSTS TABLE
  // ---------------------------------------------------------
  export function buildCostsTableData(budgetData) {
    if (!budgetData?.baseline_contract_summary) {
      return { head: [["No Costs Data"]], body: [] };
    }
    const { baseline_contract_summary: baseline, penultimate_contract_summary: penultimate, current_contract_summary: current } = budgetData;
  
    const head = [[
      "Category",
      "Baseline",
      "Snapshot A",
      "Snapshot B",
      "Snapshot A to B Variance",
      "Baseline to Snapshot B Variance",
    ]];
    const body = [];
  
    const baseCostTotals = calculateTotalsForType(baseline, "Cost");
    const penCostTotals  = calculateTotalsForType(penultimate, "Cost");
    const currCostTotals = calculateTotalsForType(current, "Cost");
  
    const {
      baselineAllowance: baselineCostAllowanceTotal,
      penultimateAllowance: penultimateCostAllowanceTotal,
      currentAllowance: currentCostAllowanceTotal,
      baselineMarkups: baselineCostMarkupsTotal,
      penultimateMarkups: penultimateCostMarkupsTotal,
      currentMarkups: currentCostMarkupsTotal,
    } = accumulateAllowanceAndMarkups(baseline, penultimate, current, "Cost");
  
    // Lump Sum Costs
    body.push([
      { content: "Lump Sum Costs", sectionHeader: true },
      { content: "" },
      { content: "" },
      { content: "" },
      { content: "" },
      { content: "" },
    ]);
  
    const currentCostItems = Object.keys(current.contract_structure.Cost || {});
    const lumpsumItems = currentCostItems.filter((item) => item.includes("LS"));
    lumpsumItems.forEach((item) => {
      const bVal = baseline.contract_structure.Cost[item] || 0;
      const pVal = penultimate.contract_structure.Cost[item] || 0;
      const cVal = current.contract_structure.Cost[item] || 0;
      const varPenToCur = calculateVariance(pVal, cVal);
      const varBaseToCur= calculateVariance(bVal, cVal);
  
      body.push([
        { content: item },
        { content: bVal.toString() },
        { content: pVal.toString() },
        { content: cVal.toString() },
        { content: varPenToCur?.toString() || "0" },
        { content: varBaseToCur?.toString() || "0" },
      ]);
    });
  
    // Lump Sum Costs Total
    body.push([
      { content: "Lump Sum Costs Total", isTotal: true },
      { content: baseCostTotals.lumpSum.toString() },
      { content: penCostTotals.lumpSum.toString() },
      { content: currCostTotals.lumpSum.toString() },
      {
        content: calculateVariance(penCostTotals.lumpSum, currCostTotals.lumpSum)?.toString() || "0",
      },
      {
        content: calculateVariance(baseCostTotals.lumpSum, currCostTotals.lumpSum)?.toString() || "0",
      },
    ]);
  
    // GMP Costs
    body.push([
      { content: "GMP Costs", sectionHeader: true },
      { content: "" },
      { content: "" },
      { content: "" },
      { content: "" },
      { content: "" },
    ]);
    const gmpItems = currentCostItems.filter((i) => i.includes("GMP"));
    gmpItems.forEach((item) => {
      const bVal = baseline.contract_structure.Cost[item] || 0;
      const pVal = penultimate.contract_structure.Cost[item] || 0;
      const cVal = current.contract_structure.Cost[item] || 0;
      const varPenToCur = calculateVariance(pVal, cVal);
      const varBaseToCur= calculateVariance(bVal, cVal);
  
      body.push([
        { content: item },
        { content: bVal.toString() },
        { content: pVal.toString() },
        { content: cVal.toString() },
        { content: varPenToCur?.toString() || "0" },
        { content: varBaseToCur?.toString() || "0" },
      ]);
    });
  
    // GMP Costs Total
    body.push([
      { content: "GMP Costs Total", isTotal: true },
      { content: baseCostTotals.gmp.toString() },
      { content: penCostTotals.gmp.toString() },
      { content: currCostTotals.gmp.toString() },
      {
        content: calculateVariance(penCostTotals.gmp, currCostTotals.gmp)?.toString() || "0",
      },
      {
        content: calculateVariance(baseCostTotals.gmp, currCostTotals.gmp)?.toString() || "0",
      },
    ]);
  
    // Allowance Costs
    body.push([
      { content: "Allowance Costs" },
      { content: baselineCostAllowanceTotal.toString() },
      { content: penultimateCostAllowanceTotal.toString() },
      { content: currentCostAllowanceTotal.toString() },
      {
        content: calculateVariance(penultimateCostAllowanceTotal, currentCostAllowanceTotal)?.toString() || "0",
      },
      {
        content: calculateVariance(baselineCostAllowanceTotal, currentCostAllowanceTotal)?.toString() || "0",
      },
    ]);
  
    // Financial Markups Costs
    body.push([
      { content: "Financial Markups Costs" },
      { content: baselineCostMarkupsTotal.toString() },
      { content: penultimateCostMarkupsTotal.toString() },
      { content: currentCostMarkupsTotal.toString() },
      {
        content: calculateVariance(penultimateCostMarkupsTotal, currentCostMarkupsTotal)?.toString() || "0",
      },
      {
        content: calculateVariance(baselineCostMarkupsTotal, currentCostMarkupsTotal)?.toString() || "0",
      },
    ]);
  
    // Final row: "Total Cost Contract Values"
    const baseTotal = baseCostTotals.lumpSum + baseCostTotals.gmp + baselineCostAllowanceTotal + baselineCostMarkupsTotal;
    const penTotal = penCostTotals.lumpSum + penCostTotals.gmp + penultimateCostAllowanceTotal + penultimateCostMarkupsTotal;
    const currTotal= currCostTotals.lumpSum + currCostTotals.gmp + currentCostAllowanceTotal + currentCostMarkupsTotal;
  
    body.push([
      { content: "Total Cost Contract Values", isTotal: true, borderType: "double" },
      { content: baseTotal.toString() },
      { content: penTotal.toString() },
      { content: currTotal.toString() },
      {
        content: calculateVariance(penTotal, currTotal)?.toString() || "0",
      },
      {
        content: calculateVariance(baseTotal, currTotal)?.toString() || "0",
      },
    ]);
  
    return { head, body };
  }
  
  // ---------------------------------------------------------
  // 3) TOTAL BUSTS/PICKUPS
  // ---------------------------------------------------------
  export function buildBustsPickupsTableData(budgetData) {
    if (!budgetData?.baseline_contract_summary) {
      return { head: [["No Data"]], body: [] };
    }
    const { baseline_contract_summary: baseline, penultimate_contract_summary: penultimate, current_contract_summary: current } = budgetData;
  
    const head = [[
      "Category",
      "Baseline",
      "Snapshot A",
      "Snapshot B",
      "Snapshot A to B Variance",
      "Baseline to Snapshot B Variance",
    ]];
    const body = [];
  
    const { baseOU, penOU, curOU, allKeys } = getOverUnderItems(baseline, penultimate, current);
  
    let baselineTotal = 0,
      penTotal = 0,
      currTotal = 0;
  
    allKeys.forEach((item) => {
      const bVal = baseOU[item] || 0;
      const pVal = penOU[item]  || 0;
      const cVal = curOU[item]  || 0;
      baselineTotal += bVal;
      penTotal      += pVal;
      currTotal     += cVal;
  
      const varPenToCur = calculateVariance(pVal, cVal) || 0;
      const varBaseToCur= calculateVariance(bVal, cVal) || 0;
      body.push([
        { content: item },
        { content: bVal.toString() },
        { content: pVal.toString() },
        { content: cVal.toString() },
        { content: varPenToCur.toString() },
        { content: varBaseToCur.toString() },
      ]);
    });
  
    const varPenToCurr = calculateVariance(penTotal, currTotal) || 0;
    const varBaseToCurr= calculateVariance(baselineTotal, currTotal) || 0;
  
    // Final total row
    body.push([
      { content: "Total Busts/Pickups", isTotal: true, borderType: "double" },
      { content: baselineTotal.toString() },
      { content: penTotal.toString() },
      { content: currTotal.toString() },
      { content: varPenToCurr.toString() },
      { content: varBaseToCurr.toString() },
    ]);
  
    return { head, body };
  }
  
  // ---------------------------------------------------------
  // 4) SUMMARY LEVEL 2 (GC/GR Spend)
  // ---------------------------------------------------------
  export function buildSummaryLevel2TableData(budgetData) {
    if (!budgetData?.baseline_contract_summary) {
      return { head: [["No Data"]], body: [] };
    }
    const { baseline_contract_summary: baseline, penultimate_contract_summary: penultimate, current_contract_summary: current } = budgetData;
  
    const head = [[
      "Category",
      "Baseline",
      "Snapshot A",
      "Snapshot B",
      "Snapshot A to B Variance",
      "Total Estimated Budget Remaining",
    ]];
    const body = [];
  
    const { baseJTD, penJTD, curJTD } = getJTDCostsItems(baseline, penultimate, current);
    // Typically only "LS-GC/GR"
    const item = "LS-GC/GR";
    const bVal = baseJTD[item] || 0;
    const pVal = penJTD[item]  || 0;
    const cVal = curJTD[item]  || 0;
    const varPenToCur = calculateVariance(pVal, cVal);
    const varBaseToCur= calculateVariance(bVal, cVal);
  
    body.push([
      { content: item },
      { content: bVal.toString() },
      { content: pVal.toString() },
      { content: cVal.toString() },
      { content: varPenToCur?.toString() || "0" },
      { content: varBaseToCur?.toString() || "0" },
    ]);
  
    return { head, body };
  }
  
  // ---------------------------------------------------------
  // 5) SUMMARY LEVEL 3 (OWNER CHANGE ORDER ANALYSIS)
  // ---------------------------------------------------------
  export function buildSummaryLevel3TableData(budgetData) {
    if (!budgetData?.baseline_contract_summary) {
      return { head: [["No Data"]], body: [] };
    }
    const {
      baseline_contract_summary: baseline,
      penultimate_contract_summary: penultimate,
      current_contract_summary: current,
    } = budgetData;
  
    const head = [[
      "Category",
      "Total Overall ($)",
      "% of Budget",
      "Monthly Variance",
    ]];
    const body = [];
  
    // total budget for current
    const currentBudget = current.contract_structure?.Budget || {};
    const totalCurrentBudget = Object.keys(currentBudget)
      .reduce((acc, k) => acc + (currentBudget[k] || 0), 0);
  
    // 1) Change Events Revenue
    const ccr = current.change_event_revenue || 0;
    const penCcr = penultimate.change_event_revenue || 0;
    const ccrVar = ccr - penCcr;
    const ccrPct = totalCurrentBudget ? (ccr / totalCurrentBudget) * 100 : 0;
    body.push([
      { content: "Change Events Revenue" },
      { content: ccr.toString() },
      { content: ccrPct.toFixed(2) + "%" },
      { content: ccrVar.toString() },
    ]);
  
    // 2) Open PCOs
    const open = current.open_pcos || 0;
    const penOpen = penultimate.open_pcos || 0;
    const openVar = open - penOpen;
    const openPct = totalCurrentBudget ? (open / totalCurrentBudget) * 100 : 0;
    body.push([
      { content: "Open PCOs" },
      { content: open.toString() },
      { content: openPct.toFixed(2) + "%" },
      { content: openVar.toString() },
    ]);
  
    // 3) Approved PCOs & PCCOs
    const apc = current.approved_pcos_pccos || 0;
    const penApc = penultimate.approved_pcos_pccos || 0;
    const apcVar = apc - penApc;
    const apcPct = totalCurrentBudget ? (apc / totalCurrentBudget) * 100 : 0;
    body.push([
      { content: "Approved PCOs & PCCOs" },
      { content: apc.toString() },
      { content: apcPct.toFixed(2) + "%" },
      { content: apcVar.toString() },
    ]);
  
    // Totals row
    const grandCurr = ccr + open + apc;
    const grandPen  = penCcr + penOpen + penApc;
    const grandVar  = grandCurr - grandPen;
    const grandPct  = totalCurrentBudget ? (grandCurr / totalCurrentBudget) * 100 : 0;
    body.push([
      { content: "Total", isTotal: true, borderType: "double" },
      { content: grandCurr.toString() },
      { content: grandPct.toFixed(2) + "%" },
      { content: grandVar.toString() },
    ]);
  
    return { head, body };
  }
  
  // ---------------------------------------------------------
  // 6) SHARED SAVINGS TABLE
  // ---------------------------------------------------------
  export function buildSharedSavingsTableData(budgetData) {
    // Check the same condition as SharedSavingsTableComponent
    if (!budgetData || !budgetData?.shared_savings_data?.shared_savings) {
      return {
        head: [["No Shared Savings Data"]],
        body: [],
      };
    }

    // Use the same structure as SharedSavingsTableComponent
    const orderedKeys = [
      "general_conditions_budget",
      "estimated_gmp_costs",
      "subtotal",
      "insurance",
      "fee",
      "projected_final_contract_value_without_savings",
      "current_projected_gmp_settings",
      "savings_added_to_gross_profit",
      "revenue_back_to_owner2",
      "total_revised_budget_after_revenue_back_to_owner",
      "total_gross_profit_after_savings_split",
      "final_gross_profit_percentage"
    ];

    const keyLabels = {
      general_conditions_budget: "General Conditions Budget (Revenue)",
      estimated_gmp_costs: "Estimated GMP Costs",
      subtotal: "Subtotal",
      insurance: "Negotiated Insurance",
      fee: "Negotiated Fee",
      projected_final_contract_value_without_savings: "Projected Final Contract Value (Without Savings)",
      current_owner_contract_value: "Current Owner Contract Value",
      projected_savings: "Projected Savings",
      current_projected_gmp_settings: "Current Projected GMP Savings",
      savings_added_to_gross_profit: "Savings Added to Gross Profit",
      revenue_back_to_owner2: "Revenue Back to Owner",
      total_revised_budget_after_revenue_back_to_owner: "Total Revised Budget After Revenue Back to Owner",
      total_gross_profit_after_savings_split: "Total Gross Profit After Savings Split",
      ls_profit: "LS Profit",
      savings_added_to_profit: "Savings Added to Profit",
      final_gross_profit_percentage: "Final Gross Profit Percentage",
    };

    const head = [[
      "Category",
      "Value"
    ]];
    const body = [];

    const sharedSavingsData = budgetData.shared_savings_data;
    
    // Process each ordered key (same logic as SharedSavingsTableComponent)
    orderedKeys.forEach((key) => {
      const value = sharedSavingsData[key];
      // Only render rows for keys that exist in the data
      if (value !== undefined) {
        let formattedValue;
        if (key === "final_gross_profit_percentage") {
          formattedValue = `${(parseFloat(value) || 0).toFixed(2)}%`;
        } else {
          formattedValue = (parseFloat(value) || 0).toFixed(2);
        }
        
        // Mark specific rows as totals for PDF formatting (bold styling)
        const isTotal = key === "subtotal" || 
                       key === "projected_final_contract_value_without_savings" ||
                       key === "total_revised_budget_after_revenue_back_to_owner" || 
                       key === "total_gross_profit_after_savings_split";
        
        body.push([
          { content: keyLabels[key] || key, isTotal },
          { content: formattedValue, isTotal }
        ]);
      }
    });

    return { head, body };
  }
  